import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  userEnabledOptions,
  userFormData,
  userRolesOptions
} from '../../../constants/user';
import styles from '../styles.module.css';
import {
  handleChangeUserType,
  handleSelectUserType,
  userProfileType
} from './types';
import { baseSchema } from './utils';
import { AdminCardForm } from '@/components/AdminToolComponents/card';
import Input from '@/components/Form/Input';
import InputSearch from '@/components/Form/InputSearch';
import { SearchResult } from '@/components/Form/InputSearch/types';
import RadioButton from '@/components/Form/RadioButton';
import Select from '@/components/Form/Select';
import { useSearchCompanies } from '@/hooks/services/Companies';
import { RootState } from '@/store';
import { formatPhoneNumber } from '@/utils/inputPhone';

export const statusContainer = ({
  showEditUser = true,
  formData = userFormData,
  handleChangeFormData = () => {}
}: {
  showEditUser?: boolean;
  formData: userProfileType;
  handleChangeFormData: handleChangeUserType;
}) => {
  return showEditUser ? (
    userEnabledOptions.map(option => (
      <RadioButton
        key={option.label}
        checked={formData.enabled === option.value}
        onChange={() => handleChangeFormData('enabled', option.value)}
        label={option.label}
      />
    ))
  ) : (
    <>
      <span className={styles.adminText}>Status</span>
      <span className={styles.adminBlueText}>
        {
          userEnabledOptions.find(option => option.value === formData.enabled)
            ?.label
        }
      </span>
    </>
  );
};

export const UserForm = ({
  formData,
  handleChangeFormData,
  handleSelectFormData,
  showEditUser = true,
  container = 'edit',
  formErrors = {}
}: {
  formData: userProfileType;
  handleChangeFormData: handleChangeUserType;
  handleSelectFormData: handleSelectUserType;
  showEditUser?: boolean;
  container?: 'edit' | 'create';
  formErrors?: { [k: string]: string };
}) => {
  const userProfile = useSelector((state: RootState) => state.userProfile);
  const getValidCompanyId = userProfile?.companyId === '250';
  const allowCompanySearch =
    container === 'create' || (showEditUser && getValidCompanyId);

  const currentUserRole = userProfile?.groups?.[0] || '';
  const isSysAdmin = currentUserRole === userRolesOptions[1].value;
  const isManager = currentUserRole === userRolesOptions[0].value;

  const getRoleSelectOptions = () => {
    if (isSysAdmin) {
      return userRolesOptions;
    }
    if (isManager) {
      return userRolesOptions.filter(
        option => option.value === userRolesOptions[2].value
      );
    }
    return userRolesOptions;
  };

  const isRoleSelectReadOnly = () => {
    if (container === 'create') {
      return true;
    }
    if (isManager) {
      return true;
    }
    return !showEditUser;
  };

  const [searchCompanyInput, setSearchCompanyInput] = useState('');
  const [companyResults, setCompanyResults] = useState<SearchResult[]>([]);

  const { data: dataCompanies, isFetching: isFetchingCompanies } =
    useSearchCompanies(searchCompanyInput, {
      useQueryOptions: {
        staleTime: Infinity,
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: searchCompanyInput.length > 0
      }
    });

  useEffect(() => {
    const companiesResults = dataCompanies?.data || [];
    setCompanyResults(companiesResults);
  }, [dataCompanies]);

  const onSearch = (query: string) => {
    if (query) {
      setSearchCompanyInput(query);
    } else {
      setCompanyResults([]);
    }
  };

  const onSelect = (item: SearchResult) => {
    handleSelectFormData([
      { field: 'company', value: item.name },
      { field: 'companyId', value: item.id }
    ]);
    setCompanyResults([]);
  };

  const onReset = () => {
    handleSelectFormData([
      { field: 'company', value: '' },
      { field: 'companyId', value: '' }
    ]);
    setSearchCompanyInput('');
    setCompanyResults([]);
  };

  const requiredFields = Object.keys(baseSchema.shape);

  const inputComponent = ({
    id,
    label,
    extraProps
  }: {
    id: string;
    label: string;
    extraProps?: any;
  }) => (
    <Input
      label={label}
      type={id === 'password' ? 'password' : 'text'}
      value={formData[id as keyof userProfileType] as string}
      onChange={(value: string) => handleChangeFormData(id, value)}
      required={requiredFields.includes(id)}
      readOnly={container === 'edit' ? true : !showEditUser}
      error={formErrors[id]}
      {...extraProps}
    />
  );

  const getPhoneValue = (phone: string) => {
    return phone?.startsWith('+1') ? phone?.replace('+1', '') : phone;
  };

  return (
    <AdminCardForm>
      {inputComponent({ id: 'username', label: 'Username' })}

      {inputComponent({ id: 'email', label: 'Email Address' })}

      {inputComponent({
        id: 'name',
        label: 'Name',
        extraProps: {
          readOnly: !showEditUser
        }
      })}

      <Select
        label="User Role"
        value={formData?.groups}
        onChange={(value: string) => handleChangeFormData('groups', value)}
        options={getRoleSelectOptions()}
        required
        readOnly={isRoleSelectReadOnly()}
        error={formErrors['groups']}
      />

      <InputSearch
        label="Company"
        placeHolder="Search company"
        defaultValue={formData?.company}
        results={companyResults}
        process={isFetchingCompanies}
        onSearch={onSearch}
        onSelect={onSelect}
        onReset={onReset}
        required
        readOnly={!allowCompanySearch}
        error={formErrors['company'] || undefined}
      />

      {inputComponent({
        id: 'phone',
        label: 'Phone Number',
        extraProps: {
          value: showEditUser
            ? getPhoneValue(formData?.phone)
            : formData?.phone
              ? '+1 ' + formatPhoneNumber(getPhoneValue(formData?.phone))
              : '',
          readOnly: !showEditUser,
          withFlag: true,
          onlyNumbers: true,
          maxLength: 10
        }
      })}

      {container === 'create' &&
        inputComponent({ id: 'password', label: 'Password' })}
    </AdminCardForm>
  );
};
